import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { Box, Stack, TableContainer, Table, TableHead, TableBody, TableRow, TableCell } from '@mui/material'
import {
  dateFormatter,
  dateTimeFormatter,
  formatAddress,
  convertShippingInfoToCityStateZip,
} from 'utils/data-manipulation/valueFormatter'
import { capAllWords } from 'utils/data-manipulation/common'
import { DISPATCH_TICKET_TYPES } from 'utils/constants'
import { Detail } from 'components/generic/printable/components/Detail'
import PrintDispatchAssetRow from 'components/generic/printable/components/PrintDispatchAssetRow'
import SignatureDateLine from 'components/generic/printable/components/SignatureDateLine'

const blankRows = [
  {
    id: 'blank-row-one',
  },
  {
    id: 'blank-row-two',
  },
]

const PrintableDispatchTicket = ({
  customerData,
  locationData,
  dispatchTicketData,
  pickupAssets,
  dropoffAssets,
  pickAndReturnAssets,
  deliveryAssets,
  liveLoadAssets,
}) => {
  const { defaultYardTimezone } = useSelector(state => state.auth)

  const { primaryContact, accountManager } = useMemo(() => {
    const { accountManagerGsUserFirstName = '', accountManagerGsUserLastName = '' } =
      customerData?.accountManagerGsUser || {}
    const accountManagerName = `${accountManagerGsUserFirstName} ${accountManagerGsUserLastName}`
    const contacts = customerData?.contacts || []
    return {
      primaryContact:
        contacts.find(contact => !!contact.isPrimaryContact && contact.locationId === dispatchTicketData?.locationId) ||
        contacts.find(contact => contact.locationId === dispatchTicketData?.locationId) ||
        contacts.find(contact => !!contact.isPrimaryContact) ||
        contacts[0],
      accountManager: accountManagerName,
    }
  }, [customerData])

  const noAssets = [dropoffAssets, pickupAssets, pickAndReturnAssets, deliveryAssets, liveLoadAssets].every(
    assets => assets.length === 0,
  )

  return (
    <>
      <Stack direction="column" sx={{ p: 1, border: '1px solid black', lineHeight: 2 }}>
        <Stack direction="row" spacing={2} width="100%" alignItems="flex-start">
          {/* Left Column: General Details */}
          <Stack width="50%">
            <Detail label="Driver" data={dispatchTicketData?.driver?.name} labelFlexBasis={50} />
            <Detail label="Customer Name" data={customerData?.displayName} labelFlexBasis={50} />
            <Detail label="Account Manager" data={accountManager} labelFlexBasis={50} />
            <Detail
              label="Primary Contact"
              data={`${primaryContact?.firstName} ${primaryContact?.lastName}`}
              labelFlexBasis={50}
            />
            <Detail label="Primary Phone" data={primaryContact?.phone1} labelFlexBasis={50} />
            <Detail label="Location Name" data={locationData?.name} labelFlexBasis={50} />
            <Detail
              label="Address"
              data={`${formatAddress(locationData)}, ${convertShippingInfoToCityStateZip(locationData)}`}
              labelFlexBasis={50}
            />
          </Stack>

          {/* Right Column: Timestamps */}
          <Stack width="50%">
            {dispatchTicketData?.startTime && (
              <Detail
                label="Start"
                data={dateTimeFormatter(dispatchTicketData.startTime, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: false,
                })}
                labelFlexBasis={50}
              />
            )}
            {dispatchTicketData?.yardDepartureDate && (
              <Detail
                label="Yard Departure"
                data={dateTimeFormatter(dispatchTicketData.yardDepartureDate, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: false,
                })}
                labelFlexBasis={50}
              />
            )}
            {dispatchTicketData?.customerArrivalDate && (
              <Detail
                label="Customer Arrival"
                data={dateTimeFormatter(dispatchTicketData.customerArrivalDate, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: false,
                })}
                labelFlexBasis={50}
              />
            )}
            {dispatchTicketData?.customerDepartureDate && (
              <Detail
                label="Customer Departure"
                data={dateTimeFormatter(dispatchTicketData.customerDepartureDate, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: false,
                })}
                labelFlexBasis={50}
              />
            )}
            {dispatchTicketData?.yardArrivalDate && (
              <Detail
                label="Yard Arrival"
                data={dateTimeFormatter(dispatchTicketData.yardArrivalDate, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: false,
                })}
                labelFlexBasis={50}
              />
            )}
            {dispatchTicketData?.completionTime && (
              <Detail
                label="Completion"
                data={dateTimeFormatter(dispatchTicketData.completionTime, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: false,
                })}
                labelFlexBasis={50}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ mt: 2, p: 1, border: '1px solid black', lineHeight: 2 }}>
        <Detail
          label="Request Date"
          data={dateTimeFormatter(dispatchTicketData?.requestDate, {
            timeZone: defaultYardTimezone,
            showTimeZone: true,
          })}
          labelFlexBasis={25}
        />
        <Detail
          label="Scheduled Date"
          data={
            dispatchTicketData?.scheduleTimeActive
              ? dateTimeFormatter(dispatchTicketData?.scheduleDate, {
                  timeZone: defaultYardTimezone,
                  showTimeZone: true,
                })
              : dateFormatter(dispatchTicketData?.scheduleDate, defaultYardTimezone)
          }
          labelFlexBasis={25}
        />
        <Detail label="Task Type" data={DISPATCH_TICKET_TYPES[dispatchTicketData?.type]?.name} labelFlexBasis={25} />
        {dispatchTicketData?.interYard && (
          <Detail
            label="Starting Yard"
            data={capAllWords(dispatchTicketData.interYard?.startLocationName)}
            labelFlexBasis={25}
          />
        )}
        {dispatchTicketData?.interYard && (
          <Detail
            label="Ending Yard"
            data={capAllWords(dispatchTicketData.interYard?.endLocationName)}
            labelFlexBasis={25}
          />
        )}
      </Stack>

      <Stack sx={{ mt: 2, p: 1, border: '1px solid black', lineHeight: 2 }}>
        <TableContainer
          sx={{
            overflow: 'hidden',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: 0, fontSize: '1rem', fontWeight: 600, width: '40%' }} align="left">
                  Asset #
                </TableCell>
                <TableCell sx={{ px: 0, fontSize: '1rem', fontWeight: 600, width: '40%' }} align="left">
                  Asset Type
                </TableCell>
                <TableCell sx={{ px: 0, fontSize: '1rem', fontWeight: 600, width: '20%' }} align="left">
                  Task
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {noAssets ? (
                blankRows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ px: 0, fontSize: '1rem' }} align="left" />
                    <TableCell sx={{ px: 0, fontSize: '1rem' }} align="left" />
                    <TableCell sx={{ px: 0, fontSize: '1rem' }} align="left">
                      {dispatchTicketData?.type === 'EXCHANGE'
                        ? 'Pickup / Dropoff'
                        : DISPATCH_TICKET_TYPES[dispatchTicketData?.type]?.name}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <>
                  {dropoffAssets.map(asset => (
                    <PrintDispatchAssetRow key={asset.id} asset={asset} label="Dropoff" />
                  ))}
                  {pickupAssets.map(asset => (
                    <PrintDispatchAssetRow key={asset.id} asset={asset} label="Pickup" />
                  ))}
                  {pickAndReturnAssets.map(asset => (
                    <PrintDispatchAssetRow key={asset.id} asset={asset} label="Pick And Return" />
                  ))}
                  {deliveryAssets.map(asset => (
                    <PrintDispatchAssetRow key={asset.id} asset={asset} label="Delivery" />
                  ))}
                  {liveLoadAssets.map(asset => (
                    <PrintDispatchAssetRow key={asset.id} asset={asset} label="Live Load" />
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Stack direction="row" spacing={2} mt={2}>
        <Stack sx={{ p: 1, border: '1px solid black', width: '50%' }}>
          <strong>Ticket Notes:</strong>
          <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{dispatchTicketData?.note}</p>
        </Stack>
        <Stack sx={{ p: 1, border: '1px solid black', width: '50%' }}>
          <strong>Recurring Site Notes:</strong>
          <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{locationData?.notes}</p>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={2} mt={2}>
        <Stack sx={{ p: 1, border: '1px solid black', width: '50%' }}>
          <strong>Driver Notes:</strong>
          <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {dispatchTicketData?.driverCompletionNotes}
          </p>
        </Stack>
        <Stack sx={{ p: 1, border: '1px solid black', width: '50%' }}>
          <strong>Dispatcher Notes:</strong>
          <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {dispatchTicketData?.dispatcherCompletionNotes}
          </p>
        </Stack>
      </Stack>
      <Box mt={5}>
        <SignatureDateLine />
      </Box>
    </>
  )
}

PrintableDispatchTicket.propTypes = {
  customerData: PropTypes.object.isRequired,
  locationData: PropTypes.object.isRequired,
  dispatchTicketData: PropTypes.object.isRequired,
  pickupAssets: PropTypes.array,
  dropoffAssets: PropTypes.array,
  pickAndReturnAssets: PropTypes.array,
  deliveryAssets: PropTypes.array,
  liveLoadAssets: PropTypes.array,
}

PrintableDispatchTicket.defaultProps = {
  pickupAssets: [],
  dropoffAssets: [],
  pickAndReturnAssets: [],
  deliveryAssets: [],
  liveLoadAssets: [],
}

export default PrintableDispatchTicket
